import React, { useState, useEffect, useMemo, useRef } from 'react';
import { matchPath, Outlet, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import TagManager from 'react-gtm-module';

import AppNavbar from '../AppNavbar/AppNavbar';
import AppNavbarMobile from '../AppNavbar/AppNavbarMobile';
import AppFooter from '../AppFooter/AppFooter';
import AppFooterMobile from '../AppFooter/AppFooterMobile';
import MobileMenu from '../AppNavbar/MobileMenu';
import SearchMenu from '../AppNavbar/SearchMenuMobile';

import { GTM_ARGS } from 'common/constants';

import style from './AppLayout.module.scss';

if (GTM_ARGS) {
  setTimeout(() => {
    GTM_ARGS && TagManager.initialize(GTM_ARGS);
  }, 500);
}

const AppLayout = (): React.ReactElement => {
  const [currentPage, setCurrentPage] = useState<string>('');
  const [isNavCollapsed, setIsNavCollapsed] = useState(true);
  const [isSearchCollapsed, setIsSearchCollapsed] = useState(true);
  const location = useLocation();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const isExplore = !!matchPath({ path: '/explore/*' }, location.pathname);
  const isDetail =
    !!matchPath({ path: '/explore/programs/:slug/*' }, location.pathname) ||
    !!matchPath({ path: '/explore/schools/:slug/*' }, location.pathname);
  const tagManagerTimer = useRef<NodeJS.Timeout>();
  const isRegistration =
    !!matchPath({ path: '/signup' }, location.pathname) ||
    !!matchPath({ path: '/login' }, location.pathname);

  useEffect(() => {
    const ignoreUrlChange = !!matchPath(
      { path: '/explore/:section/:slug/:section' },
      location.pathname
    );
    const skipPageView = location.pathname.includes('spv=1');

    if (location.pathname == '/') setCurrentPage('landing');
    else if (location.pathname == '/profile') setCurrentPage('profile');
    else if (location.pathname == '/signup' || location.pathname == '/login')
      setCurrentPage('login');
    else if (location.pathname == '/home/personal_information') setCurrentPage('personal_info');
    else if (location.pathname == '/home/preferences') setCurrentPage('preferences');
    else setCurrentPage('');

    if (GTM_ARGS && !ignoreUrlChange && !skipPageView) {
      clearTimeout(tagManagerTimer.current);
      tagManagerTimer.current = setTimeout(() => {
        TagManager.dataLayer({ dataLayer: { event: 'pageview_change' } });
      }, 1000);
    }
  }, [location.pathname]);

  const showNavbarMobile = useMemo(
    () => isMobile && !['profile', 'login', 'personal_info', 'preferences'].includes(currentPage),
    [isMobile, currentPage]
  );
  const showFooterMobile = useMemo(
    () =>
      isMobile &&
      isNavCollapsed &&
      !['profile', 'login'].includes(currentPage) &&
      isSearchCollapsed,
    [isMobile, isNavCollapsed, currentPage, isSearchCollapsed]
  );
  const showFooter = useMemo(
    () => !isMobile && !['profile', 'login'].includes(currentPage),
    [isMobile, currentPage]
  );

  return (
    <div id="layout" className={`${currentPage === 'landing' ? '' : style.layout}`}>
      {showNavbarMobile && <AppNavbar />}
      {!isMobile && !location.pathname.includes('/home') && <AppNavbar />}
      {!isNavCollapsed && isSearchCollapsed && (
        <MobileMenu
          isLanding={currentPage === 'landing'}
          setIsNavCollapsed={setIsNavCollapsed}
          isNavCollapsed={isNavCollapsed}
        />
      )}
      {!isSearchCollapsed && isNavCollapsed && (
        <SearchMenu
          isLanding={currentPage === 'landing'}
          setIsSearchCollapsed={setIsSearchCollapsed}
          isSearchCollapsed={isSearchCollapsed}
        />
      )}
      {isSearchCollapsed == isNavCollapsed && <Outlet />}
      {!location.pathname.includes('/home') && showFooter && <AppFooter />}
      {!location.pathname.includes('/home') && showFooterMobile && <AppFooterMobile />}
    </div>
  );
};

export default AppLayout;
